import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save, Send, Download, CheckCircle2 } from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 9);

export default function ExamEditor({ examId, open, onClose, onChanged }) {
  const [exam, setExam] = useState(null);
  const [qs, setQs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !examId) return;
    setLoading(true);
    api.get(`/exams/${examId}`).then((r) => { setExam(r.data); setQs(r.data.questions_json || []); })
      .catch(() => toast.error("Failed to load exam")).finally(() => setLoading(false));
  }, [open, examId]);

  const update = (i, patch) => setQs((arr) => arr.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const updateOpt = (i, oi, val) => setQs((arr) => arr.map((q, idx) => (idx === i ? { ...q, options: q.options.map((o, k) => (k === oi ? val : o)) } : q)));
  const remove = (i) => setQs((arr) => arr.filter((_, idx) => idx !== i));
  const addMcq = () => setQs((a) => [...a, { id: uid(), type: "mcq", question: "", options: ["", "", "", ""], answer_index: 0, points: 1, difficulty: "medium" }]);
  const addSub = () => setQs((a) => [...a, { id: uid(), type: "subjective", question: "", model_answer: "", points: 5, difficulty: "medium" }]);

  const save = async () => {
    setSaving(true);
    try { await api.put(`/exams/${examId}/questions`, { questions: qs }); toast.success("Saved"); onChanged && onChanged(); }
    catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const publish = async () => {
    setSaving(true);
    try {
      await api.put(`/exams/${examId}/questions`, { questions: qs });
      await api.post(`/exams/${examId}/publish`);
      toast.success("Published — paper emailed to admins");
      onChanged && onChanged(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0E1420] border-[#1E293B] text-slate-100 max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="exam-editor">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center justify-between">
            <span>Edit Exam {exam?.status === "published" ? "· Published" : "· Draft"}</span>
            <a href={`${API}/exams/${examId}/pdf`} target="_blank" rel="noreferrer" data-testid="download-pdf-link" className="inline-flex items-center gap-1.5 text-sm bg-[#111726] border border-[#1E293B] rounded-lg px-3 py-1.5 text-slate-200 hover:border-emerald-500/50 transition-colors font-normal">
              <Download className="w-4 h-4" /> PDF
            </a>
          </DialogTitle>
        </DialogHeader>

        {loading ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div> : (
          <div className="space-y-3">
            {qs.map((q, i) => (
              <div key={q.id} className="p-4 rounded-xl bg-[#090D16] border border-[#1E293B]" data-testid={`edit-q-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${q.type === "mcq" ? "bg-indigo-500/15 text-indigo-400" : "bg-amber-500/15 text-amber-400"}`}>{q.type === "mcq" ? "MCQ" : "Written"}</span>
                    <select value={q.difficulty || "medium"} onChange={(e) => update(i, { difficulty: e.target.value })} className="bg-[#111726] border border-[#1E293B] rounded text-xs px-1.5 py-1 text-slate-300 outline-none">
                      <option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option><option value="jee">JEE</option>
                    </select>
                    <input type="number" min="1" value={q.points} onChange={(e) => update(i, { points: Number(e.target.value) })} className="w-14 bg-[#111726] border border-[#1E293B] rounded text-xs px-2 py-1 text-slate-300 outline-none" title="Points" />
                  </div>
                  <button onClick={() => remove(i)} data-testid={`delete-q-${i}`} className="text-slate-500 hover:text-rose-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
                <Textarea value={q.question} onChange={(e) => update(i, { question: e.target.value })} rows={2} placeholder="Question text" className="bg-[#111726] border-[#1E293B] text-slate-100 resize-none mb-2 text-sm" />
                {q.type === "mcq" ? (
                  <div className="space-y-1.5">
                    {q.options.map((o, oi) => (
                      <label key={oi} className="flex items-center gap-2">
                        <input type="radio" name={`correct-${q.id}`} checked={q.answer_index === oi} onChange={() => update(i, { answer_index: oi })} className="accent-emerald-500" />
                        <input value={o} onChange={(e) => updateOpt(i, oi, e.target.value)} placeholder={`Option ${String.fromCharCode(65 + oi)}`} className="flex-1 bg-[#111726] border border-[#1E293B] rounded-lg px-3 py-1.5 text-sm text-slate-100 outline-none focus:border-emerald-500" />
                      </label>
                    ))}
                    <div className="text-xs text-slate-500 flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Select the radio next to the correct option.</div>
                  </div>
                ) : (
                  <Textarea value={q.model_answer} onChange={(e) => update(i, { model_answer: e.target.value })} rows={2} placeholder="Model answer / marking scheme" className="bg-[#111726] border-[#1E293B] text-slate-100 resize-none text-sm" />
                )}
              </div>
            ))}

            <div className="flex flex-wrap gap-2">
              <button onClick={addMcq} data-testid="add-mcq-button" className="inline-flex items-center gap-1.5 bg-[#111726] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-300 hover:border-indigo-500/50 transition-colors"><Plus className="w-4 h-4" /> MCQ</button>
              <button onClick={addSub} data-testid="add-subjective-button" className="inline-flex items-center gap-1.5 bg-[#111726] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-300 hover:border-amber-500/50 transition-colors"><Plus className="w-4 h-4" /> Written</button>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-[#1E293B]">
              <button onClick={save} disabled={saving} data-testid="save-exam-button" className="inline-flex items-center gap-1.5 bg-[#111726] border border-[#1E293B] rounded-xl px-4 py-2.5 text-sm text-slate-200 hover:border-emerald-500/50 transition-colors disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Draft</button>
              <button onClick={publish} disabled={saving || qs.length === 0} data-testid="publish-exam-button" className="inline-flex items-center gap-2 bg-emerald-500 text-emerald-950 font-semibold rounded-xl px-5 py-2.5 hover:bg-emerald-400 transition-colors disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Publish</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
